import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { ColorPicker } from './ui/ColorPicker';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { Toast } from './ui/Toast';
import { useToast } from '../hooks/useToast';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface Establishment {
  id: number;
  client_id: number;
  name_establishment: string;
  code_establishment_hioffice: number;
  estado_id: number;
  telephone_establishment: string | null;
  logo_path: string | null;
  font_title_id: number | null;
  font_subtitle_id: number | null;
  font_text_id: number | null;
  font_script_id: number | null;
  font_size_title: number;
  font_size_subtitle: number;
  font_size_text: number;
  export_id_articulos: number | null;
  export_id_comments: number | null;
  is_active: number;
  language: string;
}

interface Client {
  id: number;
  account_name: string;
  email_license: string;
  code_client_license: number;
}

interface State {
  id: number;
  name: string;
  cod_estado: string;
}

interface EstablishmentFormProps {
  establishment?: Establishment | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const EstablishmentForm: React.FC<EstablishmentFormProps> = ({
  establishment,
  onSuccess,
  onCancel
}) => {
  const { token } = useAuth();
  const { showToast, hideToast, toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [clients, setClients] = useState<Client[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [formData, setFormData] = useState({
    client_id: '',
    name_establishment: '',
    code_establishment_hioffice: '',
    estado_id: '',
    telephone_establishment: '',
    logo_path: '',
    export_id_articulos: '',
    export_id_comments: '',
    is_active: true,
    language: 'es'
  });

  // Cargar catálogos
  useEffect(() => {
    const loadCatalogs = async () => {
      if (!token) {
        setLoadingCatalogs(false);
        return;
      }

      setLoadingCatalogs(true);
      try {
        const [clientsRes, statesRes] = await Promise.all([
          apiFetch('catalog/clients', {}, token),
          apiFetch('catalog/states', {}, token)
        ]);

        // Si hay errores de red, mantener catálogos vacíos pero no bloquear el formulario
        const [clientsJson, statesJson] = await Promise.all([
          isNetworkErrorResponse(clientsRes) ? { data: { clients: [] } } : parseJsonResponse(clientsRes).catch(() => ({ data: { clients: [] } })),
          isNetworkErrorResponse(statesRes) ? { data: { states: [] } } : parseJsonResponse(statesRes).catch(() => ({ data: { states: [] } }))
        ]);

        if (clientsJson.data?.clients) setClients(clientsJson.data.clients);
        if (statesJson.data?.states) setStates(statesJson.data.states);
      } catch (err) {
        // Error cargando catálogos - no bloquear el formulario, solo loggear
        console.error('Error cargando catálogos:', err);
      } finally {
        setLoadingCatalogs(false);
      }
    };

      loadCatalogs();
  }, [token]);

  // Cargar datos del establecimiento si está editando
  useEffect(() => {
    if (establishment) {
      setFormData({
        client_id: establishment.client_id?.toString() || '',
        name_establishment: establishment.name_establishment || '',
        code_establishment_hioffice: establishment.code_establishment_hioffice?.toString() || '',
        estado_id: establishment.estado_id?.toString() || '',
        telephone_establishment: establishment.telephone_establishment || '',
        logo_path: establishment.logo_path || '',
        export_id_articulos: establishment.export_id_articulos?.toString() || '',
        export_id_comments: establishment.export_id_comments?.toString() || '',
        is_active: establishment.is_active === 1,
        language: establishment.language || 'es'
      });
    }
  }, [establishment]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const handleColorChange = (name: string, value: string) => {
    // Limpiar error del campo cuando el usuario cambie el color
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validar campos requeridos
    if (!formData.client_id) {
      errors.client_id = 'El cliente es requerido';
    }

    if (!formData.name_establishment.trim()) {
      errors.name_establishment = 'El nombre del establecimiento es requerido';
    }

    if (!formData.code_establishment_hioffice) {
      errors.code_establishment_hioffice = 'El código de establecimiento CloudLicense es requerido';
    }

    if (!formData.estado_id) {
      errors.estado_id = 'El estado es requerido';
    }

    // Validar formatos
    if (formData.telephone_establishment && formData.telephone_establishment.length > 15) {
      errors.telephone_establishment = 'El teléfono no puede tener más de 15 caracteres';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('No hay token de autenticación');
      showToast('No hay token de autenticación', 'error');
      return;
    }

    setError(null);
    setFieldErrors({});

    // Validar antes de enviar
    if (!validateForm()) {
      showToast('Por favor, corrige los errores en el formulario', 'error');
      return;
    }

    setLoading(true);

    try {
      const isEdit = !!establishment;
      const route = isEdit ? 'establishments/update' : 'establishments/create';

      const payload: any = {
        client_id: parseInt(formData.client_id),
        name_establishment: formData.name_establishment.trim(),
        code_establishment_hioffice: parseInt(formData.code_establishment_hioffice),
        estado_id: parseInt(formData.estado_id),
        telephone_establishment: formData.telephone_establishment?.trim() || null,
        logo_path: formData.logo_path?.trim() || null,
        export_id_articulos: formData.export_id_articulos ? parseInt(formData.export_id_articulos) : null,
        export_id_comments: formData.export_id_comments ? parseInt(formData.export_id_comments) : null,
        is_active: formData.is_active ? 1 : 0,
        language: formData.language || 'es'
      };

      if (isEdit) {
        payload.id = establishment.id;
      }

      const res = await apiFetch(route, {
        method: 'POST',
        body: JSON.stringify(payload)
      }, token);

      // Si es error de red, mostrar mensaje y mantener el formulario abierto
      if (isNetworkErrorResponse(res)) {
        const errorMessage = 'Error de conexión. Verifica tu conexión a internet e intenta de nuevo.';
        setError(errorMessage);
        showToast(errorMessage, 'error');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        // Asegurar que errorMessage sea siempre un string
        let errorMessage = 'Error al guardar establecimiento';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        } else if (json.data?.error) {
          errorMessage = typeof json.data.error === 'string' ? json.data.error : String(json.data.error);
        }
        setError(errorMessage);
        showToast(errorMessage, 'error');
        return;
      }

      showToast(
        establishment 
          ? 'Establecimiento actualizado exitosamente' 
          : 'Establecimiento creado exitosamente',
        'success'
      );
      
      // Pequeño delay para que se vea el toast antes de cerrar
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      let errorMessage = 'Error inesperado';
      if (isNetworkError(err)) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet e intenta de nuevo.';
      } else {
        errorMessage = err.message || 'Error inesperado';
      }
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loadingCatalogs) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
        <p className="text-gray-600">Cargando catálogos...</p>
      </div>
    );
  }

  return (
    <>
      {/* Toast Component */}
      <Toast
        message={toast.message}
        variant={toast.variant}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      <div className="p-6">
        {error && (
          <div className="mb-6">
            <Alert variant="error">{error}</Alert>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Información Básica */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Información Básica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Cliente"
                name="client_id"
                value={formData.client_id}
                onChange={handleChange}
                required
                error={fieldErrors.client_id}
                options={clients.map(c => ({ value: c.id, label: c.account_name }))}
              />

              <Input
                type="text"
                label="Nombre del Establecimiento"
                name="name_establishment"
                value={formData.name_establishment}
                onChange={handleChange}
                required
                error={fieldErrors.name_establishment}
                placeholder="Ej: Restaurante Centro"
              />

              <Input
                type="number"
                label="Código Establecimiento CloudLicense"
                name="code_establishment_hioffice"
                value={formData.code_establishment_hioffice}
                onChange={handleChange}
                required
                error={fieldErrors.code_establishment_hioffice}
                placeholder="12345"
              />

              <Select
                label="Estado de la República"
                name="estado_id"
                value={formData.estado_id}
                onChange={handleChange}
                required
                error={fieldErrors.estado_id}
                options={states.map(s => ({ value: s.id, label: s.name }))}
              />

              <Input
                type="tel"
                label="Teléfono"
                name="telephone_establishment"
                value={formData.telephone_establishment}
                onChange={handleChange}
                error={fieldErrors.telephone_establishment}
                placeholder="Ej: +52 555 123 4567"
              />

              <Input
                type="text"
                label="Ruta del Logo"
                name="logo_path"
                value={formData.logo_path}
                onChange={handleChange}
                placeholder="/uploads/logos/logo.png"
              />
            </div>
          </div>

          {/* IDs de Exportación */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              IDs de Exportación Hioffice
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="number"
                label="ID Exportación Artículos"
                name="export_id_articulos"
                value={formData.export_id_articulos}
                onChange={handleChange}
                placeholder="12345"
              />

            </div>
          </div>

          {/* Idioma */}
          <div className="mb-6">
            <Select
              name="language"
              label="Idioma"
              value={formData.language}
              onChange={handleChange}
              className="w-full"
              options={[
                { value: 'es', label: 'Español' },
                { value: 'en', label: 'English' }
              ]}
            />
          </div>

          {/* Estado Activo */}
          <div className="mb-6">
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
              <div>
                <span className="text-sm font-semibold text-gray-900">Establecimiento activo</span>
                <p className="text-xs text-gray-500 mt-0.5">Los establecimientos inactivos no podrán acceder al sistema</p>
              </div>
            </label>
          </div>

          {/* Botones de acción */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 mt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              isLoading={loading}
            >
              {establishment ? 'Actualizar Establecimiento' : 'Crear Establecimiento'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

